import { A } from '@solidjs/router';
import { type Component, createResource, createSignal, For, Show } from 'solid-js';
import { deleteProject, type ForkedProject, fetchForkedProjects } from '../services/api';

/**
 * ForkedProjects Page
 *
 * Shows projects that the user has forked from other users' projects.
 * These are independent copies that the user owns and can modify freely.
 */
const ForkedProjects: Component = () => {
  const [page, setPage] = createSignal(0);
  const limit = 12;

  const [projects, { refetch }] = createResource(
    () => ({ offset: page() * limit, limit }),
    fetchForkedProjects,
  );

  const [deletingId, setDeletingId] = createSignal<string | null>(null);

  const handleDeleteProject = async (projectId: string) => {
    if (
      !confirm('Are you sure you want to delete this forked project? This action cannot be undone.')
    ) {
      return;
    }
    setDeletingId(projectId);
    try {
      await deleteProject(projectId);
      refetch();
    } catch (error) {
      console.error('Failed to delete project:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = () => Math.ceil((projects()?.total || 0) / limit);

  return (
    <div>
      {/* Page Header */}
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Forked Projects
            </h1>
            <p class="text-gray-400 text-sm mt-1">
              Your independent copies of projects from the marketplace
            </p>
          </div>
          <A
            href="/marketplace"
            class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="mr-2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            Browse Marketplace
          </A>
        </div>
      </div>

      {/* Loading State */}
      <Show when={projects.loading}>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <For each={[1, 2, 3, 4, 5, 6]}>
            {() => (
              <div class="bg-gray-800/50 rounded-lg border border-gray-700 p-6 animate-pulse">
                <div class="h-6 bg-gray-700 rounded w-3/4 mb-4" />
                <div class="h-4 bg-gray-700 rounded w-full mb-2" />
                <div class="h-4 bg-gray-700 rounded w-2/3" />
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Error State */}
      <Show when={projects.error}>
        <div class="bg-red-900/20 border border-red-700 rounded-lg p-6 text-center">
          <p class="text-red-400">Failed to load projects. Please try again.</p>
          <button
            onClick={() => refetch()}
            class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white"
          >
            Retry
          </button>
        </div>
      </Show>

      {/* Empty State */}
      <Show when={!projects.loading && !projects.error && projects()?.items.length === 0}>
        <div class="bg-gray-800/50 rounded-lg border border-gray-700 p-12 text-center">
          <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-green-400"
            >
              <circle cx="12" cy="18" r="3" />
              <circle cx="6" cy="6" r="3" />
              <circle cx="18" cy="6" r="3" />
              <path d="M18 9v2c0 .6-.4 1-1 1H7c-.6 0-1-.4-1-1V9" />
              <path d="M12 12v3" />
            </svg>
          </div>
          <h2 class="text-xl font-medium text-gray-200 mb-2">No Forked Projects</h2>
          <p class="text-gray-400 mb-6 max-w-md mx-auto">
            You haven't forked any projects yet. Find interesting projects in the marketplace and
            create your own independent copies to customize and extend.
          </p>
          <A
            href="/marketplace"
            class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
          >
            Explore Marketplace
          </A>
        </div>
      </Show>

      {/* Projects Grid */}
      <Show when={!projects.loading && !projects.error && (projects()?.items.length || 0) > 0}>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <For each={projects()?.items}>
            {(project: ForkedProject) => (
              <div class="bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors overflow-hidden">
                <div class="p-6">
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex-1 min-w-0">
                      <h3 class="font-medium text-gray-200 truncate">{project.name}</h3>
                      <div class="flex items-center gap-1 mt-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="text-gray-500"
                        >
                          <circle cx="12" cy="18" r="3" />
                          <circle cx="6" cy="6" r="3" />
                          <circle cx="18" cy="6" r="3" />
                          <path d="M18 9v2c0 .6-.4 1-1 1H7c-.6 0-1-.4-1-1V9" />
                          <path d="M12 12v3" />
                        </svg>
                        <span class="text-xs text-gray-500">Forked</span>
                      </div>
                    </div>
                    <div class="flex items-center gap-1 ml-2">
                      <A
                        href={`/projects/${project.id}/edit`}
                        class="p-1 text-gray-400 hover:text-blue-400 transition-colors"
                        title="Edit project"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                          <path d="m15 5 4 4" />
                        </svg>
                      </A>
                      <button
                        onClick={() => handleDeleteProject(project.id)}
                        disabled={deletingId() === project.id}
                        class="p-1 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                        title="Delete project"
                      >
                        <Show
                          when={deletingId() !== project.id}
                          fallback={
                            <svg
                              class="animate-spin h-[18px] w-[18px]"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                              />
                              <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M3 6h18" />
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                            <line x1="10" x2="10" y1="11" y2="17" />
                            <line x1="14" x2="14" y1="11" y2="17" />
                          </svg>
                        </Show>
                      </button>
                    </div>
                  </div>
                  <p class="text-sm text-gray-400 line-clamp-2 mb-4">
                    {project.goal || project.description || 'No description'}
                  </p>
                  <div class="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <span class="px-2 py-1 bg-gray-700/50 rounded">{project.status}</span>
                    <span class="px-2 py-1 bg-gray-700/50 rounded">{project.visibility}</span>
                    <span class="text-gray-600">v{project.version}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-gray-500">
                      Created {new Date(project.createdAt).toLocaleDateString()}
                    </span>
                    <A
                      href={`/projects/${project.id}`}
                      class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      Open Project
                    </A>
                  </div>
                </div>
              </div>
            )}
          </For>
        </div>

        {/* Pagination */}
        <Show when={totalPages() > 1}>
          <div class="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page() === 0}
              class="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span class="text-gray-400 px-4">
              Page {page() + 1} of {totalPages()}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages() - 1, p + 1))}
              disabled={page() >= totalPages() - 1}
              class="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </Show>
      </Show>

      {/* Feature Info */}
      <div class="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="bg-gray-800/30 rounded-lg border border-gray-700/50 p-6">
          <div class="text-green-400 mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="18" r="3" />
              <circle cx="6" cy="6" r="3" />
              <circle cx="18" cy="6" r="3" />
              <path d="M18 9v2c0 .6-.4 1-1 1H7c-.6 0-1-.4-1-1V9" />
              <path d="M12 12v3" />
            </svg>
          </div>
          <h3 class="font-medium text-gray-200 mb-2">Independent Copy</h3>
          <p class="text-sm text-gray-400">
            Forked projects are your own copies. Make any changes without affecting the original.
          </p>
        </div>

        <div class="bg-gray-800/30 rounded-lg border border-gray-700/50 p-6">
          <div class="text-blue-400 mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              <path d="m15 5 4 4" />
            </svg>
          </div>
          <h3 class="font-medium text-gray-200 mb-2">Full Control</h3>
          <p class="text-sm text-gray-400">
            Edit, extend, or completely reimagine the project to fit your specific needs.
          </p>
        </div>

        <div class="bg-gray-800/30 rounded-lg border border-gray-700/50 p-6">
          <div class="text-purple-400 mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <path d="M2 10h20" />
            </svg>
          </div>
          <h3 class="font-medium text-gray-200 mb-2">Start Private</h3>
          <p class="text-sm text-gray-400">
            Forks start as private. Publish to the marketplace when you're ready to share.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForkedProjects;
